import {Injectable} from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import {
  catchError,
  EMPTY,
  from,
  Observable,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import {TranslocoService} from '@jsverse/transloco';
import {LocalStorageService} from "../services/local-storage.service";
import {NotificationService} from "../services/notification.service";
import {PathEnum} from "../shared/enums/path-enum";
import {NavController} from "@ionic/angular/standalone";


// Match su segmenti di path con confine di parola: esclude gli endpoint di auth
// (/login, /auth, /logout, /token, /passkey) senza catturare endpoint applicativi
// che contengono la sottostringa 'token' (es. /cittadino/push-token).
// Coerente con Forbidden403Interceptor (SKIP_403_PATTERN).
const SKIP_401_PATTERN = /\/(login|auth|logout|token|passkey)\b/;

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {

    private isRedirectingToLogin = false;

    constructor(
        private localStorageService: LocalStorageService,
        private notificationService: NotificationService,
        private navCtrl: NavController,
        private transloco: TranslocoService,
    ) {
    }

    intercept(httpRequest: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        // Skip auth-related endpoints (match per segmento, non per sottostringa)
        if (SKIP_401_PATTERN.test(httpRequest.url)) {
            return next.handle(httpRequest);
        }

        if (httpRequest.method !== 'OPTIONS') {
            return this.handleRequest(httpRequest, next);
        } else {
            return next.handle(httpRequest);
        }
    }

    handleRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError(
                (error: HttpErrorResponse): Observable<HttpEvent<any>> => {
                    if (error.status === 401) {
                        if (this.isRedirectingToLogin) {
                            // Redirect al login già in corso: chiudiamo lo stream senza emettere
                            // un valore di successo fittizio (EMPTY completa senza `next`), così i
                            // subscriber non ricevono `null` come fosse una risposta valida.
                            return EMPTY;
                        }
                        this.isRedirectingToLogin = true;
                        return this.localStorageService.cleanPreferenceToken$().pipe(
                            switchMap(() => from(this.notificationService.closeAll())),
                            switchMap(() => from(
                                this.navCtrl.navigateRoot(PathEnum.LOGIN, {
                                    replaceUrl: true,
                                    state: {sessionExpired: true}
                                }).then(
                                    () => this.isRedirectingToLogin = false,
                                    () => this.isRedirectingToLogin = false
                                )
                            )),
                            tap(() => this.notificationService.openToast({
                                message: this.transloco.translate('common.toast.session_expired_login'),
                                color: 'warning'
                            })),
                            // La sessione è scaduta: nessuna risposta valida da inoltrare al chiamante.
                            switchMap(() => EMPTY)
                        );
                    } else {
                        // I 403 non competono a questo interceptor: li gestisce
                        // Forbidden403Interceptor (redirect soft) o, per il GET profilo,
                        // UserSessionService (logout forzato). Qui vanno solo propagati.
                        return throwError(() => error);
                    }
                }));
    }
}
